import { useEffect, useState } from "react";
import {
    changeAppState,
    getAppState,
    useAppState,
} from "../../../../common/appState";
import { useDelayedState } from "../../../../common/hooks/useDelayedState";
import { useUser } from "../../../../common/hooks/useUser";
import { fetchGet } from "../../../../common/util/fetch";
import { RightPanel } from "../../Panel/RightPanel";
import { OtherUser, User } from "../../User/types";
import { MessageContent } from "./Content";
import { readMessages } from "./components/ConversationArea/functions";
import { MuteSwitch } from "./components/MuteSwitch";
import { fetchRecentMessages } from "./fetchRecentMessages";
import { getMessagesPerDays } from "./getMessagesPerDay";
import {
    InitMessagesResponse,
    MessagesPerDays,
    RecentMessagesResponse,
} from "./types";

const transitionMilliseconds = 1000;

const initialMessageCountToShow = 30; // This should be a number that can show the scrollbar.
const additionalMessageCountToShow = 15;

let lastFetchedCount = initialMessageCountToShow;

export default function MessagePanel() {
    const [panelState, setPanelState] = useAppState("messagePanelState");
    const isOpen = panelState !== "close";
    const targetUserId = isOpen ? panelState.targetUserId : undefined;

    const shouldReload = isOpen && panelState.reload;
    useEffect(() => {
        /**
         * リロード機能使用時は、openOrReloadMessagePanel()関数を利用するようにする
         */
        if (shouldReload) {
            if (targetUserId) {
                // リロード要求受信時
                changeAppState("messagePanelState", {
                    targetUserId: 0, // targetUserIdを0にする
                    reload: { previousState: panelState },
                });
                return;
            }
            // targetUserIdを0にした後
            if (targetUserId === 0 && typeof panelState.reload === "object") {
                changeAppState(
                    "messagePanelState",
                    { ...panelState.reload.previousState, reload: undefined } // 元の状態に戻す
                );
            }
        }
    }, [shouldReload, targetUserId]);

    const { user } = useUser();
    const [messagesCountToShow, setMessagesCountToShow] = useState(
        initialMessageCountToShow
    );
    const [currentlySentMessageNo, setCurrentlySentMessageNo] = useState(0);
    const { targetUser, messages, setMessages } = useLoad(
        user,
        targetUserId,
        messagesCountToShow,
        isOpen,
        setCurrentlySentMessageNo
    );

    const showMore = async () => {
        if (!user || !targetUserId) {
            return;
        }
        const newMessagesCountToShow =
            messagesCountToShow + additionalMessageCountToShow;
        if (newMessagesCountToShow !== lastFetchedCount) {
            lastFetchedCount = newMessagesCountToShow;
            await getExpandedNumberMessages(
                user.userId,
                targetUserId,
                newMessagesCountToShow,
                setMessages
            );
            setMessagesCountToShow(newMessagesCountToShow);
        }
    };

    const { delayedState } = useDelayedState(
        panelState,
        state => state !== "close",
        transitionMilliseconds
    );

    const loaded = targetUser && user;
    return (
        <RightPanel
            panelName="MessagePanel"
            open={isOpen}
            onClose={() => {
                setPanelState("close");
                setMessagesCountToShow(initialMessageCountToShow);
                lastFetchedCount = initialMessageCountToShow;
            }}
            panelWidth={500}
            globalPanelState={panelState}
            isChildrenUnmounted={!loaded || targetUser.userId !== targetUserId}
        >
            {user && targetUser && (
                <MuteSwitch
                    fromUserId={user.userId}
                    targetUserId={targetUser.userId}
                />
            )}

            {loaded && delayedState !== "close" && (
                <MessageContent
                    targetUser={targetUser}
                    user={user}
                    messages={messages}
                    setMessages={setMessages}
                    showMore={showMore}
                    messagesCountToShow={messagesCountToShow}
                    currentlySentMessageNo={currentlySentMessageNo}
                    setCurrentlySentMessageNo={setCurrentlySentMessageNo}
                    panelState={delayedState}
                />
            )}
        </RightPanel>
    );
}

async function fetchInitMessages(
    myUserId: number,
    targetUserId: number,
    num: number
): Promise<InitMessagesResponse> {
    return fetchGet<
        { myUserId: number; targetUserId: number; num: number },
        InitMessagesResponse
    >("api/Message/GetInitMessages", { myUserId, targetUserId, num });
}

let lastFetchTime: number = 0;
async function fetchMessagesSometimes(
    myUserId: number,
    targetUserId: number,
    num: number
): Promise<RecentMessagesResponse> {
    const now = Number(new Date());
    if (now - lastFetchTime < 3000) {
        return { error: "fetching too often" };
    }
    lastFetchTime = now;

    return fetchGet<
        { myUserId: number; targetUserId: number; num: number },
        RecentMessagesResponse
    >("api/Message/GetRecentMessages", { myUserId, targetUserId, num });
}

function useLoad(
    user: User | undefined,
    targetUserId: number | undefined,
    messagesCountToShow: number,
    isOpen: boolean,
    setCurrentlySentMessageNo: (messageNo: number) => void
) {
    const [targetUser, setTargetUser] = useState<OtherUser | undefined>(
        undefined
    );
    const [messages, setMessages] = useState<MessagesPerDays>({});

    useEffect(() => {
        if (!user || !targetUserId || !isOpen) {
            return;
        }
        fetchInitMessages(user.userId, targetUserId, messagesCountToShow).then(
            async result => {
                if ("error" in result) {
                    return;
                }
                setTargetUser(result.targetUser);
                const messagesPerDay = getMessagesPerDays(result.messages);
                setMessages(messagesPerDay);
                const lastMessageNo = getLastMessageNo(messagesPerDay);
                setCurrentlySentMessageNo(lastMessageNo);

                // Read invisible unread messages (In case they are more than initialMessageCountToShow)
                const invisibleUnreadMessages = getAppState()
                    .unreadMessages.filter(
                        unreadMessages =>
                            unreadMessages.fromUserId === targetUserId
                    )
                    .filter(
                        unreadMessage =>
                            !result.messages.some(
                                m => m.messageNo === unreadMessage.messageNo
                            )
                    );
                if (invisibleUnreadMessages.length > 0) {
                    await readMessages(
                        targetUserId,
                        user.userId,
                        invisibleUnreadMessages.map(m => m.messageNo),
                        messagesCountToShow
                    );
                    changeAppState(
                        "unreadMessages",
                        getAppState().unreadMessages.filter(
                            m =>
                                !(
                                    m.fromUserId === targetUserId &&
                                    m.messageNo === m.messageNo
                                )
                        )
                    );
                }
            }
        );
    }, [targetUserId, !!user, isOpen]);

    useEffect(() => {
        if (!user || !targetUserId || !isOpen) {
            return;
        }
        const intervalId = setInterval(() => {
            getUnreadMessages(
                user.userId,
                targetUserId,
                messagesCountToShow,
                setMessages
            );
        }, 10 * 1000);
        return () => {
            clearInterval(intervalId);
        };
    }, [targetUserId, !!user, isOpen, messagesCountToShow]);

    return {
        targetUser,
        messages,
        setMessages,
    };
}

function getUnreadMessages(
    userId: number,
    targetUserId: number,
    messagesCountToShow: number,
    setMessages: (messages: MessagesPerDays) => void
) {
    fetchMessagesSometimes(userId, targetUserId, messagesCountToShow).then(
        result => {
            if (
                "error" in result ||
                result.messages.every(
                    m => m.fromUserId === userId || m.isRead // Already read all messages
                )
            ) {
                return;
            }
            setMessages(getMessagesPerDays(result.messages));
        }
    );
}

async function getExpandedNumberMessages(
    userId: number,
    targetUserId: number,
    messagesCountToShow: number,
    setMessages: (messages: MessagesPerDays) => void
) {
    const result = await fetchRecentMessages(
        userId,
        targetUserId,
        messagesCountToShow
    );
    if ("error" in result) {
        return;
    }
    setMessages(getMessagesPerDays(result.messages));
}

function getLastMessageNo(messages: MessagesPerDays): number {
    const messageArrays = Object.values(messages);
    const lastDateMessages =
        messageArrays.length > 0 ? messageArrays[messageArrays.length - 1] : [];
    const lastMessage =
        lastDateMessages.length > 0
            ? lastDateMessages[lastDateMessages.length - 1]
            : undefined;
    return lastMessage?.messageNo ?? 0;
}
